import { LuHash, LuTable, LuChartColumn, LuChartLine, LuChartPie } from 'react-icons/lu';
import type { ArranjoBloco, VisualRelatorio } from '../services/RelatorioPersonalizadoService';

/* A miniatura do painel: a planta do relatório, em tamanho de selo.

   É prévia de ESTRUTURA, não de dado — mostra quantos blocos o relatório tem,
   onde ficam e de que formato são, e não o resultado deles. A escolha foi
   medida: desenhar o número de verdade custaria uma consulta por relatório
   (21 chamadas e ~700ms para os dez de hoje, 81 para quarenta), e o número
   numa caixa de 90px é pequeno demais para se confiar nele — número lido
   errado é pior que número ausente.

   Também é a prévia que nunca mente: ela não tem como ficar desatualizada,
   porque desenha o que está salvo, não o que estava no banco há dez minutos.

   Desenho em CSS Grid puro, sem medir nada em JavaScript: a grade do painel
   já é de 12 colunas, e `grid-column: x+1 / span w` põe cada bloco no lugar
   exato. O que o JS faz é só decidir a ALTURA da linha, para o selo ter
   sempre a mesma altura total independentemente de o painel ter 4 ou 12
   linhas — miniatura de altura variável desalinharia os cartões da galeria. */

const ICONE: Record<VisualRelatorio, React.ReactNode> = {
    numero: <LuHash />,
    tabela: <LuTable />,
    barras: <LuChartColumn />,
    linha: <LuChartLine />,
    pizza: <LuChartPie />,
    lista: <LuTable />,
};

/** Altura do selo. Fixa: cartões de painéis diferentes têm que alinhar o nome. */
const ALTURA = 96;
const GAP = 3;

export default function MiniaturaPainel({ arranjo }: { arranjo: ArranjoBloco[] }) {
    if (arranjo.length === 0) return <div className="rl-mini is-vazia" aria-hidden="true" />;

    const linhas = arranjo.reduce((max, b) => Math.max(max, b.y + b.h), 1);

    /* Linhas em fração, não em pixel fixo: com altura fixa por linha o selo
       ficava com 114, 118 ou 120px conforme o painel, e o nome do relatório
       caía em alturas diferentes entre cartões vizinhos da mesma fileira.
       Com `1fr` o selo tem SEMPRE 96px e o que muda é a espessura das faixas,
       que é justamente o que a miniatura quer contar. */
    return (
        <div
            className="rl-mini"
            style={{ gridTemplateRows: `repeat(${linhas}, 1fr)`, gap: GAP, height: ALTURA }}
            aria-hidden="true"
        >
            {arranjo.map((b, i) => (
                <span
                    key={i}
                    /* O ícone acompanha o tamanho do bloco: num bloco de 12 colunas
                       por 4 linhas — que é a cara da maioria dos relatórios de um
                       bloco só — um ícone de 11px se perde no meio da tarja. */
                    className={`rl-mini-b ${b.w >= 6 && b.h >= 3 ? 'is-grande' : ''}`}
                    style={{
                        gridColumn: `${b.x + 1} / span ${b.w}`,
                        gridRow: `${b.y + 1} / span ${b.h}`,
                    }}
                >
                    {ICONE[b.visual] ?? ICONE.tabela}
                </span>
            ))}
        </div>
    );
}
